import Link from 'next/link'
import Image from 'next/image'
import cn from 'clsx'
import s from './Post.module.scss'
import React, { FC, useEffect, useState } from 'react'
import {
  FullHeightSection,
  Text,
  CustomImage,
  Container,
  Button,
  useUI,
} from '@components/ui'
import { CustomImageProps } from '@components/ui/CustomImage/CustomImage'
import { Post } from '@commerce/types/post'
import Posts from './PostsView'
import { SEO } from '@components/common'

type PostProps = {
  data: Post
  posts: Post[]
  children?: any
}

type SocialsType = {
  title: string
  url: string
}

const Socials = ({ title, url }: SocialsType) => {
  const [linkCopied, setLinkCopied] = useState(false)

  const pageLink = 'https://lore.cc' + url

  const handleCopyLink = (e: React.SyntheticEvent, text: string) => {
    e.preventDefault()
    if (window.isSecureContext && 'clipboard' in navigator) {
      navigator.clipboard.writeText(text)
      setLinkCopied(true)
      window.setTimeout(() => {
        linkCopied && setLinkCopied(false)
      }, 2000)
    } else {
      console.error(
        `Clipboard API requires secure context or your browser doesn't support clipboard API`
      )
    }
  }
  return (
    <div className={s.socials}>
      <ul>
        <li>
          <Link href={`https://www.facebook.com/sharer.php?u=${pageLink}`}>
            <a>
              <Image src="/facebook.svg" layout="fill" alt="Facebook" />
            </a>
          </Link>
        </li>
        <li>
          <Link
            href={`https://www.linkedin.com/sharing/share-offsite/?url=${pageLink}`}
          >
            <a>
              <Image src="/linkedin.svg" layout="fill" alt="Linkedin" />
            </a>
          </Link>
        </li>
        <li>
          <Link href={`mailto:?subject=The Logbook: ${title}&body=${pageLink}`}>
            <a>
              <Image src="/mail.svg" layout="fill" alt="Email" />
            </a>
          </Link>
        </li>
        <li>
          <Link href={pageLink}>
            <a onClick={(e) => handleCopyLink(e, pageLink)}>
              <Image src="/link.svg" layout="fill" alt="Copy Link" />
            </a>
          </Link>
          {linkCopied && (
            <div className={s.linkCopied}>
              <Text variant="smallText">Link Copied</Text>
            </div>
          )}
        </li>
      </ul>
    </div>
  )
}

const Post: FC<PostProps> = ({ data, posts, children }) => {
  const { setContainerFullWidth } = useUI()

  useEffect(() => {
    setContainerFullWidth()
  }, [])

  return (
    <>
      <div className={cn(s.root, s.postRoot)}>
        <Text variant="heading" className={s.blogTitle}>
          The Logbook
        </Text>
        <Text variant="smallText" className={s.subject}>
          {data.meta_keywords}
        </Text>

        <div className={s.postHeadingContainer}>
          <Text variant="pageHeading" className={s.postTitle}>
            {data.title}
          </Text>

          {data.url && <Socials title={data.title} url={data.url} />}
        </div>
        <div className={s.postBody}>
          <Text html={data.body} />
        </div>

        <div className={s.bodySocials}>
          {data.url && <Socials title={data.title} url={data.url} />}
        </div>

        <section className={s.latest}>
          <Text variant="pageHeading">Latest</Text>

          <div className={s.latestPosts}>
            {posts.map((post, index) => (
              <React.Fragment key={`post-${index}`}>
                {post.url && (
                  <div className={s.latestPost}>
                    <Link href={post.url.replace('the-logbook/', '')}>
                      <a>
                        <div className={s.previewThumbnail}>
                          {post.thumbnail_path && (
                            <Image
                              src={post.thumbnail_path}
                              layout="fill"
                              alt="Post"
                              objectFit="cover"
                            />
                          )}
                        </div>
                        <div className={s.previewText}>
                          <Text className={s.subject} variant="smallText">
                            {post.meta_keywords}
                          </Text>
                          <Text variant="sectionHeading2">{post.title}</Text>
                        </div>
                      </a>
                    </Link>
                  </div>
                )}
              </React.Fragment>
            ))}

            <Link href="/logbook">
              <a>
                <Button className={s.viewAll}>Read More</Button>
              </a>
            </Link>
          </div>
        </section>
      </div>

      <SEO
        title={data.title + ' | LoreOne'}
        description={data.meta_description}
        openGraph={{
          type: 'website',
          title: data.title,
          description: data.meta_description,
          images: [
            {
              url: data.thumbnail_path,
              width: '800',
              height: '600',
              alt: data.title,
            },
          ],
        }}
      />
    </>
  )
}

export default Post
